export const MARKETING_CHANNEL_LABELS: Record<string, string> = {
  google_ads: 'Google Ads',
  paid_social: 'Paid social',
  organic_search: 'Organic search',
  direct: 'Direct',
  email: 'Email',
  social: 'Social',
  referral: 'Referral',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  phone: 'Phone',
  previous_student: 'Previous student',
  model_club: 'Model Club',
  rpl: 'RPL',
  course_page: 'Course page',
  online_course: 'Online course',
  unknown: 'Unknown',
};

export type AttributionRow = {
  label: string;
  value: string;
  multiline?: boolean;
  href?: string;
};

const GOOGLE_CLICK_KEYS = ['gclid', 'gbraid', 'wbraid', '_gcl'] as const;
const FACEBOOK_CLICK_KEYS = ['fbclid', '_fbc', '_fbp', 'fbp', 'fbc'] as const;
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

const NON_ATTRIBUTION_KEYS = new Set([
  'acct',
  'checkout',
  'discount_code',
  'discount_id',
  'discount_valid',
  'email',
  'kits',
  'learner_count',
  'learner_ids',
  'name',
  'onsite_cpd_package',
  'orderId',
  'phone',
  'preferences',
  'purchased_by_user_id',
  'purchased_on_behalf_of',
  'quizResult',
  'rpl',
  'surname',
  'title',
  'type',
  'user_id',
  'courses',
  'voucherCode',
]);

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

export function formatMarketingChannel(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }

  return MARKETING_CHANNEL_LABELS[value] || value.replace(/_/g, ' ');
}

export function formatConversionType(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }

  return value.replace(/_/g, ' ').replace(/-/g, ' ');
}

function pathToUrl(path: unknown): string | undefined {
  if (typeof path !== 'string' || !path.trim()) {
    return undefined;
  }

  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  return `https://cosmetic.college${path.startsWith('/') ? path : `/${path}`}`;
}

function hasAnyKey(
  record: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  return keys.some((key) => {
    const value = record[key];
    return value !== undefined && value !== null && value !== '';
  });
}

export function inferLegacyMarketingChannel(
  record: Record<string, unknown> | null | undefined,
): string | null {
  const resolved = asRecord(record);
  if (!resolved) {
    return null;
  }

  if (hasAnyKey(resolved, GOOGLE_CLICK_KEYS)) {
    return 'google_ads';
  }

  if (hasAnyKey(resolved, FACEBOOK_CLICK_KEYS)) {
    const medium = String(resolved.utm_medium || '').toLowerCase();
    if (
      resolved.fbclid ||
      ['cpc', 'ppc', 'paid', 'paid_social'].includes(medium)
    ) {
      return 'paid_social';
    }
    return 'social';
  }

  if (hasAnyKey(resolved, UTM_KEYS)) {
    const medium = String(resolved.utm_medium || '').toLowerCase();
    if (medium === 'email') return 'email';
    if (medium === 'organic' || medium === 'organic_search')
      return 'organic_search';
    if (['cpc', 'ppc', 'paid'].includes(medium)) return 'google_ads';
    if (medium === 'social') return 'social';
  }

  if (typeof resolved.source === 'string') {
    const source = resolved.source.toLowerCase();
    if (source === 'sms') return 'sms';
    if (source === 'phone') return 'phone';
    if (source === 'whatsapp') return 'whatsapp';
  }

  if (resolved.referralId) {
    return 'referral';
  }

  return null;
}

function pushRow(
  rows: AttributionRow[],
  label: string,
  value: unknown,
  options: { multiline?: boolean; href?: string } = {},
) {
  if (value === undefined || value === null || value === '') {
    return;
  }

  if (typeof value === 'boolean') {
    rows.push({
      label,
      value: value ? 'Yes' : 'No',
      ...options,
    });
    return;
  }

  const formatted = String(value).trim();
  if (!formatted) {
    return;
  }

  rows.push({
    label,
    value: formatted,
    ...options,
  });
}

export function buildAttributionRows(
  data: Record<string, unknown> | null | undefined,
): AttributionRow[] {
  const record = asRecord(data);
  if (!record) {
    return [];
  }

  const preferences = asRecord(record.preferences) || {};
  const rows: AttributionRow[] = [];

  let marketingChannel = formatMarketingChannel(record.marketing_channel);
  if (!marketingChannel) {
    const inferred = inferLegacyMarketingChannel(record);
    if (inferred) {
      marketingChannel = `${formatMarketingChannel(inferred)} (from stored click/UTM data)`;
    }
  }

  pushRow(rows, 'Marketing channel', marketingChannel);
  pushRow(
    rows,
    'Conversion type',
    formatConversionType(record.conversion_type),
  );
  pushRow(
    rows,
    'Campaign source',
    record.campaign_source ?? preferences.campaign_source,
  );
  pushRow(rows, 'Landing page', record.landing_page, {
    href: pathToUrl(record.landing_page),
  });
  pushRow(rows, 'Source page', record.page_source ?? preferences.source_page, {
    href: pathToUrl(record.page_source ?? preferences.source_page),
  });
  pushRow(rows, 'Referrer', record.referrer, { multiline: true });
  pushRow(rows, 'Previous student', record.previous_student);
  pushRow(rows, 'Offline placement', record.source_placement);
  pushRow(rows, 'Referral ID', record.referralId);

  pushRow(rows, 'UTM source', record.utm_source);
  pushRow(rows, 'UTM medium', record.utm_medium);
  pushRow(rows, 'UTM campaign', record.utm_campaign);
  pushRow(rows, 'UTM term', record.utm_term);
  pushRow(rows, 'UTM content', record.utm_content);

  pushRow(rows, 'Google Click ID (gclid)', record.gclid, { multiline: true });
  pushRow(rows, 'Google wbraid', record.wbraid, { multiline: true });
  pushRow(rows, 'Google gbraid', record.gbraid, { multiline: true });
  pushRow(rows, 'Google Ads cookie (_gcl_aw)', record._gcl, {
    multiline: true,
  });
  pushRow(rows, 'Meta fbclid', record.fbclid, { multiline: true });
  pushRow(rows, 'Meta cookie (_fbc)', record._fbc, { multiline: true });
  pushRow(rows, 'Meta cookie (_fbp)', record._fbp, { multiline: true });
  pushRow(rows, 'Meta fbc', record.fbc, { multiline: true });
  pushRow(rows, 'Meta fbp', record.fbp, { multiline: true });
  pushRow(rows, 'Source tag', record.source);
  pushRow(rows, 'Campaign tag', record.campaign);
  pushRow(rows, 'Landing page URL', record.url, {
    href: pathToUrl(record.url),
    multiline: true,
  });
  pushRow(rows, 'Page', record.page);

  pushRow(rows, 'Ad group ID', record.adgroup);
  pushRow(rows, 'Ad group name', record.adgroup_name);
  pushRow(rows, 'Ad ID', record.ad_id);
  pushRow(rows, 'Ad name', record.ad_name);
  pushRow(rows, 'Ad set ID', record.ad_set_id);
  pushRow(rows, 'Ad set name', record.ad_set_name);
  pushRow(rows, 'Campaign ID', record.campaign_id);
  pushRow(rows, 'Campaign name', record.campaign_name);
  pushRow(rows, 'Keyword', record.keyword);
  pushRow(rows, 'Device', record.device);

  const shownLabels = new Set(rows.map((row) => row.label));
  Object.entries(record).forEach(([key, value]) => {
    if (NON_ATTRIBUTION_KEYS.has(key)) {
      return;
    }

    if (preferences[key] !== undefined) {
      return;
    }

    if (value === undefined || value === null || value === '') {
      return;
    }

    if (typeof value === 'object') {
      return;
    }

    const label = key.replace(/_/g, ' ');
    if (shownLabels.has(label)) {
      return;
    }

    pushRow(rows, label, value, {
      multiline: String(value).length > 48,
    });
    shownLabels.add(label);
  });

  return rows;
}

export function mergeAttributionRecords(
  ...sources: Array<Record<string, unknown> | null | undefined>
): Record<string, unknown> {
  return Object.assign({}, ...sources.filter(Boolean));
}

function isEmptyMergeValue(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * Shallow merge that never overwrites a non-empty target value.
 * Empty strings / null / undefined in source are ignored.
 */
export function mergePreservingExisting(
  target: Record<string, unknown> | null | undefined,
  source: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(asRecord(target) || {}) };
  const incoming = asRecord(source);
  if (!incoming) return out;

  for (const [key, value] of Object.entries(incoming)) {
    if (isEmptyMergeValue(value)) continue;
    if (isEmptyMergeValue(out[key])) {
      out[key] = value;
    }
  }

  return out;
}

/**
 * Attribution / tracking fields only - excludes operational lead/enrollment keys
 * (title, courses, preferences, purchased_on_behalf_of, etc.).
 */
export function extractMergeableAttribution(
  data: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const record = asRecord(data);
  if (!record) return {};

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (NON_ATTRIBUTION_KEYS.has(key)) continue;
    if (isEmptyMergeValue(value)) continue;
    if (typeof value === 'object') continue;
    out[key] = value;
  }
  return out;
}

export type LeadAttributionCandidate = {
  id?: string;
  created_at?: string;
  metadata?: Record<string, unknown> | null;
  source?: string | null;
};

export function hasStoredAttributionData(
  data: Record<string, unknown> | null | undefined,
): boolean {
  const record = asRecord(data);
  if (!record) {
    return false;
  }

  if (buildAttributionRows(record).length > 0) {
    return true;
  }

  return (
    hasAnyKey(record, GOOGLE_CLICK_KEYS) ||
    hasAnyKey(record, FACEBOOK_CLICK_KEYS) ||
    hasAnyKey(record, UTM_KEYS) ||
    hasAnyKey(record, [
      'marketing_channel',
      'referralId',
      'landing_page',
      'referrer',
      'source',
      'campaign',
      'adgroup',
      'keyword',
    ])
  );
}

export function hasAttributionRows(
  data: Record<string, unknown> | null | undefined,
): boolean {
  return buildAttributionRows(data).length > 0;
}

export function pickBestLeadAttribution(
  leads: LeadAttributionCandidate[] = [],
  enrollmentCreatedAt?: string | null,
) {
  const withData = leads.filter((lead) =>
    hasStoredAttributionData(lead.metadata),
  );

  if (withData.length === 0) {
    return null;
  }

  const withinWindow = (candidates: LeadAttributionCandidate[]) => {
    if (!enrollmentCreatedAt) return candidates;
    const enrollmentTime = new Date(enrollmentCreatedAt).getTime();
    if (Number.isNaN(enrollmentTime)) return candidates;
    const beforeEnrollment = candidates.filter(
      (lead) =>
        lead.created_at &&
        new Date(lead.created_at).getTime() <= enrollmentTime,
    );
    return beforeEnrollment.length > 0 ? beforeEnrollment : candidates;
  };

  const byFirstTouch = (
    a: LeadAttributionCandidate,
    b: LeadAttributionCandidate,
  ) =>
    new Date(a.created_at || 0).getTime() -
    new Date(b.created_at || 0).getTime();

  const byLastTouch = (
    a: LeadAttributionCandidate,
    b: LeadAttributionCandidate,
  ) =>
    new Date(b.created_at || 0).getTime() -
    new Date(a.created_at || 0).getTime();

  const scoped = withinWindow(withData);
  const withGoogleClick = scoped.filter((lead) =>
    hasAnyKey(asRecord(lead.metadata) || {}, GOOGLE_CLICK_KEYS),
  );

  // Prefer original first-touch Google click; last-touch only when none stored.
  if (withGoogleClick.length > 0) {
    return withGoogleClick.sort(byFirstTouch)[0];
  }

  return scoped.sort(byLastTouch)[0];
}
